import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import { GetRoute } from "../../Custom_hooks/Routes";
import { useMediaQuery } from "../../Custom_hooks/Custom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Users,
  UserCheck,
  BarChart3,
  Smile,
  Trophy,
  Clock3,
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const Card = ({ className = "", children }) => (
  <div
    className={`rounded-3xl bg-white border border-indigo-100 shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ className = "", children }) => (
  <div className={className}>{children}</div>
);

const Button = ({ className = "", children, ...props }) => (
  <button
    {...props}
    className={`px-5 py-3 rounded-2xl bg-[#4f46e5] text-white font-semibold hover:bg-[#4338ca] transition-all duration-300 ${className}`}
  >
    {children}
  </button>
);

const chartData = [
  { month: "Jan", students: 200 },
  { month: "Feb", students: 350 },
  { month: "Mar", students: 500 },
  { month: "Apr", students: 700 },
  { month: "May", students: 900 },
];

const admins = [
  { id: 1, name: "SAT Mock Test", role: "Maths", status: "Active" },
  { id: 2, name: "Weekly Quiz", role: "English", status: "Active" },
  { id: 3, name: "Full Length Test", role: "Practice", status: "Inactive" },
];

function Dashboard() {
  const auth = useSelector((state) => state.user.auth);
  const isMobile = useMediaQuery("(max-width:430px)");
  const [test, setTests] = useState([]);

  useEffect(() => {
    GetRoute(
      `refreshUser/${auth?.data._id}`,
      () => {},
      (data) => {
        setTests(data.assignedTests);
      },
    );
  }, []);

  const handleFidTestCount = (status) => {
    const tests = test.filter((test) => test.testStatus === status);
    return tests.length;
  };

  const handleFindTestTypeCount = (type) => {
    const newType = type.toUpperCase();

    const tests = test.filter((t) =>
      t.testType.toUpperCase().includes(newType),
    );

    return tests.length;
  };

  const completed = handleFidTestCount("Completed") || 0;

  const total =
    handleFidTestCount("Completed") + handleFidTestCount("Pending") || 0;

  const percentage = total === 0 ? 0 : (completed / total) * 100;

  const stats = [
    {
      title: "Total Tests",
      value: handleFidTestCount("Completed") + handleFidTestCount("Pending"),
      icon: Users,
    },
    {
      title: "Total Done",
      value: handleFidTestCount("Completed"),
      icon: BarChart3,
    },
    {
      title: "Practice Tests",
      value: handleFindTestTypeCount("Practice"),
      icon: UserCheck,
    },
    {
      title: "Mock Tests",
      value: handleFindTestTypeCount("Mock"),
      icon: Smile,
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5f7ff] p-3 md:p-6 overflow-y-auto">
      {/* HERO SECTION */}
      <motion.div
        initial={{ opacity: 0, y: -25 }}
        animate={{ opacity: 1, y: 0 }}
        className="
          mb-8
          rounded-[32px]
          bg-gradient-to-r
          from-[#4f46e5]
          via-[#6366f1]
          to-[#818cf8]
          p-5
          md:p-8
          shadow-xl
          flex
          flex-col
          md:flex-row
          md:items-center
          md:justify-between
          gap-5
        "
      >
        <div className="flex items-center gap-4">
          <div
            className="
              w-14
              h-14
              md:w-16
              md:h-16
              rounded-2xl
              bg-white/20
              flex
              items-center
              justify-center
              backdrop-blur-md
            "
          >
            <Smile className="text-white w-8 h-8" />
          </div>

          <div>
            <h1 className="text-xl md:text-3xl font-bold text-white">
              Welcome {auth?.data?.name}
            </h1>

            <p className="text-white/80 text-sm md:text-base">
              {auth?.data?.email}
            </p>
          </div>
        </div>

        <div
          className="
            w-16
            h-16
            md:w-20
            md:h-20
            rounded-full
            bg-white/20
            flex
            items-center
            justify-center
            text-white
            text-2xl
            md:text-3xl
            font-bold
            backdrop-blur-md
          "
        >
          {auth?.data?.name?.[0]?.toUpperCase()}
        </div>
      </motion.div>

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="hover:shadow-xl transition-all duration-300">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    {stat.title}
                  </p>

                  <h2 className="text-3xl font-bold text-[#4f46e5] mt-2">
                    {stat.value}
                  </h2>
                </div>

                <div
                  className="
                    w-14
                    h-14
                    rounded-2xl
                    bg-indigo-100
                    flex
                    items-center
                    justify-center
                  "
                >
                  <stat.icon className="w-7 h-7 text-[#4f46e5]" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* LEARNING SECTION */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-8">
        {/* MOTIVATION */}
        <Card className="xl:col-span-2 overflow-hidden bg-gradient-to-r from-[#4f46e5] to-[#6366f1] text-white">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3">
              <Trophy className="w-7 h-7" />
              <h2 className="text-2xl font-bold">You're doing amazing 🚀</h2>
            </div>

            <p className="text-white/80 mb-6">
              Continue your progress and complete your remaining tests.
            </p>

            <Button className="bg-white text-black hover:bg-gray-100">
              <span style={{ color: "black" }}>Resume Learning</span>
            </Button>
          </CardContent>
        </Card>

        {/* COMPLETION */}
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center text-center">
            <div className="relative h-[160px] w-[160px] flex items-center justify-center">
              <svg
                width="160"
                height="160"
                className="absolute top-0 left-0 -rotate-90"
              >
                {/* BACKGROUND */}
                <circle
                  cx="80"
                  cy="80"
                  r="65"
                  stroke="#e0e7ff"
                  strokeWidth="12"
                  fill="none"
                />

                {/* PROGRESS */}
                <circle
                  cx="80"
                  cy="80"
                  r="65"
                  stroke="#4f46e5"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={408}
                  strokeDashoffset={408 - (408 * percentage) / 100}
                />
              </svg>

              {/* CENTER TEXT */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <h2 className="text-3xl font-bold text-[#4f46e5] leading-none">
                  {percentage.toFixed(0)}%
                </h2>

                <p className="text-xs text-gray-500 mt-2 leading-none">
                  Completed
                </p>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500">Overall Test Progress</p>
          </CardContent>
        </Card>
      </div>

      {/* ACHIEVEMENTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-[#4f46e5] mb-5">
              Achievements
            </h3>

            <div className="flex flex-wrap gap-3">
              {["Starter", "5-Day Streak", "Quiz Master", "Fast Learner"].map(
                (badge) => (
                  <div
                    key={badge}
                    className="
                    px-4
                    py-2
                    rounded-2xl
                    bg-indigo-100
                    text-[#4f46e5]
                    font-semibold
                    text-sm
                  "
                  >
                    {badge}
                  </div>
                ),
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 flex flex-col justify-center items-center text-center h-full">
            <div
              className="
                w-16
                h-16
                rounded-2xl
                bg-indigo-100
                flex
                items-center
                justify-center
                mb-4
              "
            >
              <Clock3 className="text-[#4f46e5] w-8 h-8" />
            </div>

            <p className="text-gray-500 text-sm">Time Spent Today</p>

            <h2 className="text-4xl font-bold text-[#4f46e5] mt-2">1h 25m</h2>

            <p className="text-xs text-gray-400 mt-1">Great consistency!</p>
          </CardContent>
        </Card>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-8">
        {/* LINE CHART */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-xl font-bold text-[#4f46e5] mb-5">
              Learning Progress
            </h3>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />

                <Line
                  type="monotone"
                  dataKey="students"
                  stroke="#4f46e5"
                  strokeWidth={4}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* BAR CHART */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-xl font-bold text-[#4f46e5] mb-5">
              Practice Activity
            </h3>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />

                <Bar
                  dataKey="students"
                  fill="#4f46e5"
                  radius={[10, 10, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* UPCOMING TESTS */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-[#4f46e5]">
                Upcoming Tests
              </h3>

              <Button className="hidden md:block">View All</Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-indigo-100">
                    <th className="text-left py-4 text-gray-500 font-semibold">
                      Test Name
                    </th>

                    <th className="text-left py-4 text-gray-500 font-semibold">
                      Subject
                    </th>

                    <th className="text-left py-4 text-gray-500 font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.id}
                      className="border-b border-gray-100 hover:bg-indigo-50 transition"
                    >
                      <td className="py-4 font-medium text-gray-700">
                        {admin.name}
                      </td>

                      <td className="text-gray-600">{admin.role}</td>

                      <td>
                        <span
                          className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                            admin.status === "Active"
                              ? "bg-green-100 text-green-600"
                              : "bg-red-100 text-red-500"
                          }`}
                        >
                          {admin.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

export default Dashboard;
